import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export enum EnemyType {
  BASIC = 'basic',      // 10 points
  MEDIUM = 'medium',    // 20 points
  ADVANCED = 'advanced' // 30 points
}

export class Enemy implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private type: EnemyType;
  private animationTime: number = 0;
  private animationFrame: number = 0;
  private shootTimer: number = 0;
  private shootChance: number;
  private pointValue: number;
  private color: string;

  constructor(
    id: string,
    x: number,
    y: number,
    type: EnemyType,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: 16, y: 12 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.type = type;
    
    // Set properties based on enemy type
    switch (type) {
      case EnemyType.BASIC:
        this.pointValue = 10;
        this.color = '#FFFFFF';
        this.shootChance = 0.3; // 30% chance to shoot when selected
        break;
      case EnemyType.MEDIUM:
        this.pointValue = 20;
        this.color = '#BFCE72'; // Yellow
        this.shootChance = 0.4; // 40% chance to shoot when selected
        break;
      case EnemyType.ADVANCED:
        this.pointValue = 30;
        this.color = '#883932'; // Red
        this.shootChance = 0.5; // 50% chance to shoot when selected
        break;
    }
    
    // Random initial shoot timer to stagger enemy fire
    this.shootTimer = Math.random() * 2;
  }

  public update(deltaTime: number): void {
    // Update animation
    this.animationTime += deltaTime;
    if (this.animationTime >= 0.5) {
      this.animationTime = 0;
      this.animationFrame = (this.animationFrame + 1) % 2;
    }
    
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Update shoot timer
    this.shootTimer += deltaTime;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    this.drawEnemySprite(centerX, centerY);
  }

  private drawEnemySprite(centerX: number, centerY: number): void {
    // Different sprites for different enemy types and animation frames
    let pattern: number[][];
    
    switch (this.type) {
      case EnemyType.BASIC:
        pattern = this.animationFrame === 0 ? [
          [0,0,1,0,0,0,0,1,0,0],
          [0,0,0,1,0,0,1,0,0,0],
          [0,0,1,1,1,1,1,1,0,0],
          [0,1,1,0,1,1,0,1,1,0],
          [1,1,1,1,1,1,1,1,1,1],
          [1,0,1,1,1,1,1,1,0,1],
          [1,0,1,0,0,0,0,1,0,1],
          [0,0,0,1,1,0,1,1,0,0]
        ] : [
          [0,0,1,0,0,0,0,1,0,0],
          [1,0,0,1,0,0,1,0,0,1],
          [1,0,1,1,1,1,1,1,0,1],
          [1,1,1,0,1,1,0,1,1,1],
          [1,1,1,1,1,1,1,1,1,1],
          [0,1,1,1,1,1,1,1,1,0],
          [0,0,1,0,0,0,0,1,0,0],
          [0,1,0,0,0,0,0,0,1,0]
        ];
        break;
        
      case EnemyType.MEDIUM:
        pattern = this.animationFrame === 0 ? [
          [0,0,0,0,1,1,0,0,0,0],
          [0,0,0,1,1,1,1,0,0,0],
          [0,0,1,1,1,1,1,1,0,0],
          [0,1,1,0,1,1,0,1,1,0],
          [1,1,1,1,1,1,1,1,1,1],
          [1,0,1,1,1,1,1,1,0,1],
          [1,0,1,0,0,0,0,1,0,1],
          [0,0,0,1,0,0,1,0,0,0]
        ] : [
          [0,0,0,0,1,1,0,0,0,0],
          [0,0,0,1,1,1,1,0,0,0],
          [0,0,1,1,1,1,1,1,0,0],
          [0,1,1,0,1,1,0,1,1,0],
          [1,1,1,1,1,1,1,1,1,1],
          [0,1,1,1,1,1,1,1,1,0],
          [0,0,1,0,0,0,0,1,0,0],
          [1,0,0,0,0,0,0,0,0,1]
        ];
        break;
        
      case EnemyType.ADVANCED:
        pattern = this.animationFrame === 0 ? [
          [0,0,0,1,1,1,1,0,0,0],
          [0,1,1,1,1,1,1,1,1,0],
          [1,1,1,1,1,1,1,1,1,1],
          [1,1,1,0,1,1,0,1,1,1],
          [1,1,1,1,1,1,1,1,1,1],
          [0,0,1,1,0,0,1,1,0,0],
          [0,1,0,1,1,1,1,0,1,0],
          [1,0,1,0,0,0,0,1,0,1]
        ] : [
          [0,0,0,1,1,1,1,0,0,0],
          [0,1,1,1,1,1,1,1,1,0],
          [1,1,1,1,1,1,1,1,1,1],
          [1,1,1,0,1,1,0,1,1,1],
          [1,1,1,1,1,1,1,1,1,1],
          [0,1,0,1,1,1,1,0,1,0],
          [1,0,1,0,0,0,0,1,0,1],
          [0,1,0,0,0,0,0,0,1,0]
        ];
        break;
        
      default:
        pattern = [[1]];
    }
    
    // Draw the pattern
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - 10 + col * 2;
          const y = centerY - 8 + row * 2;
          this.renderManager.fillRect(x, y, 2, 2, this.color);
        }
      }
    }
  }

  public setVelocity(velocity: Vector2): void {
    this.velocity = { ...velocity };
  }

  public shouldShoot(): boolean {
    if (this.shootTimer < 1) return false; // Don't shoot immediately
    return Math.random() < this.shootChance;
  }

  public getShootPosition(): Vector2 {
    return {
      x: this.position.x + this.size.x / 2,
      y: this.position.y + this.size.y
    };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public getPointValue(): number {
    return this.pointValue;
  }

  public getType(): EnemyType {
    return this.type;
  }

  public destroy(): void {
    this.active = false;
    // Play explosion sound (different pitch based on enemy type)
    this.audioManager.playExplosion();
  }
}